/**
 * WidgetAuditLogger — write-path audit trail for Event widgets.
 *
 * Writes lightweight audit records to the `widget_audit` subcollection under
 * the owning user's document:
 *
 *   users/{userDocId}/widget_audit/{autoId}
 *
 * changeType values:
 *   'widget_created'           — first time a widget ID appears in Firestore
 *   'widget_updated'           — subsequent saves of existing widget data
 *   'widget_overwritten'       — a save that replaces non-empty data with empty/partial data
 *   'widget_deleted'           — widget removed from the cards array
 *   'widget_repaired'          — admin or system job normalised corrupted widget state
 *   'widget_validation_failed' — a save was BLOCKED by the write guard (nothing was written)
 *
 * Each record includes:
 *   userId, widgetId, widgetType, changeType, timestamp, source
 *   beforeSnapshot / afterSnapshot (optional, trimmed to key fields)
 *
 * This module is intentionally fire-and-forget. Audit failures must NEVER
 * block the primary write path.
 */

import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const isDevelopment = process.env.NODE_ENV === 'development';

/** Fields kept in a snapshot to keep Firestore records small. */
const SNAPSHOT_FIELDS = [
  'id', 'type', 'eventTitle', 'eventDate', 'eventDescription',
  'eventLocation', 'rsvpEnabled', 'reminderSettings',
];

/**
 * Log a widget lifecycle event to Firestore.
 *
 * @param {string} userDocId    - Firestore document ID of the owning user.
 * @param {string} widgetId     - The widget's stable ID.
 * @param {string} widgetType   - e.g. 'Event', 'Countdown', 'TextBox'.
 * @param {string} changeType   - See valid values above.
 * @param {Object} [options]    - Optional extra fields:
 *   @param {Object}  [options.before]    - Widget state BEFORE the change.
 *   @param {Object}  [options.after]     - Widget state AFTER the change.
 *   @param {string}  [options.source]    - 'UI' | 'admin' | 'system' (default: 'UI')
 *   @param {string}  [options.caller]    - Function name that initiated the save.
 *   @param {string[]}[options.errors]    - Validation errors (for widget_validation_failed).
 *   @param {string}  [options.adminId]   - Admin UID for admin-initiated actions.
 */
export async function logWidgetEvent(userDocId, widgetId, widgetType, changeType, options = {}) {
  if (!userDocId || !widgetId) return;

  const {
    before = null,
    after = null,
    source = 'UI',
    caller = null,
    errors = null,
    adminId = null,
    uid = null,
    // legacy flat metadata support (previous callers pass a plain object)
    ...legacyMetadata
  } = options;

  try {
    const db = getFirestore();
    const auditRef = collection(db, 'users', userDocId, 'widget_audit');

    const record = {
      widgetId,
      widgetType: widgetType || 'unknown',
      changeType,
      source,
      timestamp: serverTimestamp(),
    };

    if (caller) record.caller = caller;
    if (adminId) record.adminId = adminId;
    if (uid) record.uid = uid;
    if (errors && errors.length) record.errors = errors;

    if (before) record.beforeSnapshot = snapshotWidget(before);
    if (after) record.afterSnapshot = snapshotWidget(after);

    // Absorb any legacy flat metadata fields (strings / booleans only)
    for (const [key, value] of Object.entries(legacyMetadata)) {
      if (value === undefined) continue;
      if (['before', 'after', 'source', 'caller', 'errors', 'adminId'].includes(key)) continue;
      record[key] = typeof value === 'string' && value.length > 200
        ? value.slice(0, 200) + '…'
        : value;
    }

    await addDoc(auditRef, record);
  } catch (err) {
    // Never propagate — audit failures must not interrupt the user experience.
    if (isDevelopment) {
      console.warn('[WidgetAuditLogger] Failed to write audit record:', err);
    }
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

function snapshotWidget(widget) {
  if (!widget || typeof widget !== 'object') return null;
  const snap = {};
  for (const key of SNAPSHOT_FIELDS) {
    if (widget[key] !== undefined) snap[key] = widget[key];
  }
  return snap;
}
